import {
  EmailAuthProvider,
  GoogleAuthProvider,
  reauthenticateWithCredential,
  signInWithEmailAndPassword,
  signInWithPopup,
  updatePassword,
} from 'firebase/auth';
import { normalizeEmail } from '@brands/shared';
import { firebase } from '../lib/firebase';

export async function loginWithPassword(email: string, password: string): Promise<void> {
  await signInWithEmailAndPassword(firebase().auth, normalizeEmail(email), password);
}

/** Acceso del equipo de la agencia con su cuenta de Google. */
export async function loginWithGoogle(): Promise<void> {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });
  await signInWithPopup(firebase().auth, provider);
}

/** Cambio de contraseña del propio usuario (pide la actual para confirmar). */
export async function changeOwnPassword(current: string, next: string): Promise<void> {
  const user = firebase().auth.currentUser;
  if (!user?.email) throw Object.assign(new Error('sin sesión'), { code: 'auth/no-current-user' });
  await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, current));
  await updatePassword(user, next);
}

/** Solo rutas internas, para que `next` no sirva como redirección abierta. */
export function safeNext(next: string | null | undefined): string {
  return next && next.startsWith('/') && !next.startsWith('//') ? next : '/';
}

/** Mensajes en español para los errores de Firebase Auth que puede ver un cliente. */
export function authErrorMessage(error: unknown): string {
  const code = typeof error === 'object' && error && 'code' in error ? String(error.code) : '';
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
    case 'auth/invalid-email':
      return 'Correo o contraseña incorrectos.';
    case 'auth/user-disabled':
      return 'Este acceso está desactivado. Escribe a tu contacto en Brands&People.';
    case 'auth/too-many-requests':
      return 'Demasiados intentos. Espera unos minutos y vuelve a intentarlo.';
    case 'auth/weak-password':
      return 'La contraseña nueva es muy débil: usa al menos 10 caracteres con letras y números.';
    case 'auth/requires-recent-login':
      return 'Por seguridad, vuelve a entrar e inténtalo de nuevo.';
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return 'Se cerró la ventana de Google antes de terminar.';
    case 'auth/popup-blocked':
      return 'El navegador bloqueó la ventana de Google. Permite ventanas emergentes e inténtalo de nuevo.';
    case 'auth/network-request-failed':
      return 'Sin conexión. Revisa tu internet e inténtalo de nuevo.';
    case 'auth/unauthorized-domain':
      return 'Este dominio no está autorizado para iniciar sesión.';
    default:
      return 'No pudimos completar el acceso. Inténtalo de nuevo.';
  }
}
